import {
  v1AreaTypeControllerCreate,
  type CreateSolutionsRequest,
  v1ProjectsControllerAddAreas,
  v1ProjectsControllerAddBulkSolutions,
  v1ProjectsControllerAddImageToScenario,
  v1ProjectsControllerAddRisks,
  v1ProjectsControllerCreateCustomScenario,
  v1ProjectsControllerCreateCustomSolution,
  v1ProjectsControllerCreateScenario,
  v1ProjectsControllerDeleteArea,
  v1ProjectsControllerDeleteRisk,
  v1ProjectsControllerDeleteScenario,
  v1ProjectsControllerDeleteScenarioImage,
  v1ProjectsControllerDeleteSolution,
  v1ProjectsControllerGetScenarioImage,
  v1ProjectsControllerGetScenarios,
  v1ProjectsControllerGetSuggestedAreaTypes,
  v1ProjectsControllerGetSuggestedRiskTypesForArea,
  v1ProjectsControllerGetSuggestedScenariosForRisk,
  v1ProjectsControllerGetSuggestedSolutionsForScenario,
  v1ProjectsControllerUpdateAreaName,
  v1ProjectsControllerUpdateRisk,
  v1ProjectsControllerUpdateScenario,
  v1RiskTypeControllerList,
  type UpdateRiskRequest,
  type UpdateScenarioRequest,
} from '~/generated/api'
import type {
  Area,
  AreaType,
  Project,
  RemovedRisk,
  Risk,
  RiskType,
  Scenario,
  ScenarioImage,
  ScenarioType,
  Solution,
  SolutionType,
} from '~/models'
import { normalizeSolutionType } from '~/models'
import { withUploadSafeName } from '~/utils/apiError'
import { AnalyticsLogger } from '~/utils/analytics'

interface ApiResult<T> {
  data?: T
  error?: unknown
  response?: Response
}

interface ScenarioImageContext {
  projectId: string
  areaId: string
  riskId: string
  scenarioId: string
  images: ScenarioImage[]
}

interface LoadScenarioImageOptions {
  accessToken?: string
  context?: ScenarioImageContext
  publicAssetUrl?: (path: string) => string | null
}

export const useAssessmentStore = defineStore('assessment', () => {
  const appConfig = useAppConfig()
  const selectedArea = ref<Area | null>(null)
  const selectedRisk = ref<Risk | null>(null)
  const selectedScenario = ref<Scenario | null>(null)
  const suggestedAreas = ref<AreaType[]>([])
  const suggestedRisks = ref<RiskType[]>([])
  const allRiskTypes = ref<RiskType[]>([])
  const suggestedSolutionTypes = ref<SolutionType[]>([])
  const scenarioImages = ref(new Map<string, File[]>())
  const cache = new Map<string, unknown>()

  function projectContext() {
    const projectStore = useProjectStore()
    const refs = storeToRefs(projectStore)
    return { projectStore, ...refs }
  }

  /** Shows why a request failed, as every project request does. */
  function notifyError(requestError: unknown, responseStatus?: number) {
    projectContext().projectStore.notifyRequestError(requestError, responseStatus)
  }

  async function request<T>(
    loading: Ref<boolean>,
    operation: () => Promise<ApiResult<T>>,
  ): Promise<{ data?: T, success: boolean }> {
    const { error } = projectContext()
    loading.value = true
    error.value = null
    try {
      const result = await operation()
      if (result.error !== undefined) {
        notifyError(result.error, result.response?.status)
        return { success: false }
      }
      return { data: result.data, success: true }
    }
    catch (requestError) {
      notifyError(requestError)
      return { success: false }
    }
    finally {
      loading.value = false
    }
  }

  function adminHeaders() {
    return { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY }
  }

  function resetAssessment() {
    selectedArea.value = null
    selectedRisk.value = null
    selectedScenario.value = null
    suggestedAreas.value = []
    suggestedRisks.value = []
    suggestedSolutionTypes.value = []
    scenarioImages.value.clear()
    cache.clear()
  }

  function clearAssessmentCache() {
    cache.clear()
  }

  async function fetchSuggestedAreas(fetching: Ref<boolean>, id?: string) {
    const { selectedProject } = projectContext()
    const projectId = id ?? selectedProject.value?.id
    if (!projectId)
      return

    const key = `areas:${projectId}`
    const cached = cache.get(key) as AreaType[] | undefined
    if (cached) {
      suggestedAreas.value = structuredClone(cached)
      return
    }
    const { data } = await request(fetching, () =>
      v1ProjectsControllerGetSuggestedAreaTypes({ path: { id: projectId } }))
    const areas = data?.areaTypes ?? []
    cache.set(key, structuredClone(areas))
    suggestedAreas.value = areas
  }

  async function fetchSuggestedRiskTypes(
    fetching: Ref<boolean>,
    areaTypeId?: string | number,
  ) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !areaTypeId)
      return []

    const key = `risks:${selectedProject.value.id}:${areaTypeId ?? ''}`
    const cached = cache.get(key) as RiskType[] | undefined
    if (cached) {
      suggestedRisks.value = [...cached]
      return [...cached]
    }
    const { data } = await request(fetching, () =>
      v1ProjectsControllerGetSuggestedRiskTypesForArea({
        path: { id: selectedProject.value!.id },
        query: { areaTypeId: String(areaTypeId) },
      }))
    const risks = data?.riskTypes ?? []
    suggestedRisks.value = risks
    cache.set(key, risks)
    return risks
  }

  async function fetchAllRiskTypes(fetching: Ref<boolean>) {
    const { data } = await request(fetching, () => v1RiskTypeControllerList())
    allRiskTypes.value = data?.riskTypes ?? []
    return data
  }

  async function fetchSuggestedSolutionTypes(
    fetching: Ref<boolean>,
    id?: string,
    refetch?: boolean,
  ) {
    const { projectStore, selectedProject } = projectContext()
    const projectId = id ?? selectedProject.value?.id
    const areaId = selectedArea.value?.id
    const riskId = selectedRisk.value?.id
    const scenarioId = selectedScenario.value?.id
    if (!projectId || !areaId || !riskId || !scenarioId)
      return

    const key = `solutions:${projectId}:${areaId}:${riskId}:${scenarioId}`
    const cached = cache.get(key) as SolutionType[] | undefined
    if (cached && !refetch) {
      suggestedSolutionTypes.value = [...cached]
      return
    }
    const { data } = await request(fetching, () =>
      v1ProjectsControllerGetSuggestedSolutionsForScenario({
        path: { areaId, id: projectId, riskId, scenarioId },
      }))
    let solutions = (data?.solutionTypes ?? []).map(normalizeSolutionType)
    const ids = solutions.map(solution => solution.id)
    if (ids.length) {
      const images = await projectStore.getBatchSolutionImages(fetching, ids)
      solutions = solutions.map(solution => ({
        ...solution,
        images: images[solution.id] ?? [],
      }))
    }
    suggestedSolutionTypes.value = solutions
    cache.set(key, [...solutions])
  }

  async function createAreasAndRisks(
    posting: Ref<boolean>,
    body: Area[],
    id?: string,
  ) {
    const { selectedProject, showNotification } = projectContext()
    const projectId = id ?? selectedProject.value?.id
    if (!selectedProject.value || !projectId || !body.length)
      return

    const existingIds = selectedProject.value.areas
      ?.map(area => area.areaType?.id)
      .filter((areaId): areaId is string => Boolean(areaId)) ?? []
    const linkedIds = body
      .map(area => area.areaType?.id)
      .filter((areaId): areaId is string => Boolean(areaId))
      .filter(areaId => !existingIds.includes(areaId))

    if (linkedIds.length) {
      const { data } = await request(posting, () => v1ProjectsControllerAddAreas({
        body: { areaTypeIds: linkedIds },
        headers: adminHeaders(),
        path: { id: projectId },
      }))
      if (data)
        selectedProject.value = data as unknown as Project
    }

    const customTypes = body
      .filter(area => !area.id && !area.areaType?.id && area.name)
      .map(area => ({ name: area.name!, isCustom: true } as AreaType))
    for (const customType of customTypes) {
      const { data } = await request(posting, () => v1AreaTypeControllerCreate({
        body: { name: customType.name, isCustom: true },
        headers: adminHeaders(),
      }))
      customType.id = data?.id
    }
    const customIds = customTypes
      .map(area => area.id)
      .filter((areaId): areaId is string => Boolean(areaId))
    if (customIds.length) {
      const { data } = await request(posting, () => v1ProjectsControllerAddAreas({
        body: { areaTypeIds: customIds },
        headers: adminHeaders(),
        path: { id: projectId },
      }))
      if (data)
        selectedProject.value = data as unknown as Project
    }

    await Promise.all(body.map(async (sourceArea) => {
      if (!sourceArea.risks?.length)
        return
      const area = selectedProject.value?.areas?.find(candidate =>
        (candidate.name ?? candidate.areaType?.name)
        === (sourceArea.name ?? sourceArea.areaType?.name))
      if (!area?.id)
        return
      const existingRiskIds = area.risks?.map(risk => risk.riskType.id) ?? []
      const riskTypeIds = sourceArea.risks
        .map(risk => risk.riskType.id)
        .filter(riskTypeId => !existingRiskIds.includes(riskTypeId))
      if (riskTypeIds.length) {
        await request(posting, () => v1ProjectsControllerAddRisks({
          body: { riskTypeIds },
          headers: adminHeaders(),
          path: { areaId: area.id!, id: projectId },
        }))
      }
    }))
    showNotification.value = true
  }

  async function patchRisk(
    patching: Ref<boolean>,
    body?: Partial<Risk>,
    id?: string | number,
  ) {
    const { selectedProject, showNotification } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !(id ?? selectedRisk.value?.id))
      return
    await request(patching, () => v1ProjectsControllerUpdateRisk({
      body: (body ?? selectedRisk.value) as UpdateRiskRequest,
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: String(id ?? selectedRisk.value!.id),
      },
    }))
    showNotification.value = true
  }

  async function deleteRisk(deleting: Ref<boolean>, id?: string) {
    const { selectedProject, showNotification } = projectContext()
    const projectId = selectedProject.value?.id
    const areaId = selectedArea.value?.id
    const riskId = id ?? selectedRisk.value?.id
    if (!projectId || !areaId || !riskId)
      return
    await request(deleting, () => v1ProjectsControllerDeleteRisk({
      headers: adminHeaders(),
      path: { areaId, id: projectId, riskId },
    }))
    showNotification.value = true
  }

  async function deleteArea(deleting: Ref<boolean>, areaId: string) {
    const { selectedProject, showNotification } = projectContext()
    if (!selectedProject.value?.id || !areaId)
      return
    await request(deleting, () => v1ProjectsControllerDeleteArea({
      headers: adminHeaders(),
      path: { areaId, id: selectedProject.value!.id },
    }))
    showNotification.value = true
  }

  async function patchScenario(
    patching: Ref<boolean>,
    body: Partial<Scenario>,
    id?: string | number,
  ) {
    const { selectedProject, showNotification } = projectContext()
    const scenarioId = id ?? selectedScenario.value?.id
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !scenarioId)
      return
    await request(patching, () => v1ProjectsControllerUpdateScenario({
      body: body as UpdateScenarioRequest,
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId: String(scenarioId),
      },
    }))
    showNotification.value = true
  }

  /**
   * Renames a custom scenario through its project. The API may give the
   * scenario a new scenario type, so callers take the type from the result.
   */
  async function renameScenario(
    renaming: Ref<boolean>,
    scenarioId: string,
    name: string,
  ) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id)
      return { success: false }
    return request(renaming, () => v1ProjectsControllerUpdateScenario({
      body: { name },
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId,
      },
    }))
  }

  async function deleteScenario(deleting: Ref<boolean>, id?: string | number) {
    const { selectedProject, showNotification } = projectContext()
    const scenarioId = id ?? selectedScenario.value?.id
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !scenarioId)
      return
    const { success } = await request(deleting, () => v1ProjectsControllerDeleteScenario({
      headers: adminHeaders(),
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId: String(scenarioId),
      },
    }))
    if (success && selectedRisk.value) {
      selectedRisk.value.scenarios = selectedRisk.value.scenarios
        ?.filter(scenario => scenario.id !== String(scenarioId))
      if (selectedScenario.value?.id === String(scenarioId))
        selectedScenario.value = null
    }
    showNotification.value = true
  }

  async function deleteSolution(deleting: Ref<boolean>, solutionId: string) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !selectedScenario.value?.id)
      return
    const { data } = await request(deleting, () => v1ProjectsControllerDeleteSolution({
      headers: adminHeaders(),
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId: selectedScenario.value!.id!,
        solutionId,
      },
    }))
    return data
  }

  async function createBulkSolution(
    posting: Ref<boolean>,
    body: CreateSolutionsRequest,
  ) {
    const { selectedProject, showNotification } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !selectedScenario.value?.id)
      return
    const { data } = await request(posting, () => v1ProjectsControllerAddBulkSolutions({
      body,
      headers: adminHeaders(),
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId: selectedScenario.value!.id!,
      },
    }))
    showNotification.value = true
    return data as unknown as Scenario | undefined
  }

  async function createCustomScenarios(posting: Ref<boolean>, names: string[]) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !names.length)
      return
    await Promise.all(names.map(name => request(posting, () =>
      v1ProjectsControllerCreateCustomScenario({
        body: { name },
        headers: adminHeaders(),
        path: {
          areaId: selectedArea.value!.id!,
          id: selectedProject.value!.id,
          riskId: selectedRisk.value!.id!,
        },
      }))))
  }

  async function createCustomSolution(posting: Ref<boolean>, name: string) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !selectedScenario.value?.id)
      return
    const { data } = await request(posting, () => v1ProjectsControllerCreateCustomSolution({
      body: { name },
      headers: adminHeaders(),
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        riskId: selectedRisk.value!.id!,
        scenarioId: selectedScenario.value!.id!,
      },
    }))
    return data as unknown as Solution | undefined
  }

  async function uploadScenarioImages(posting: Ref<boolean>, file: File) {
    const { selectedProject } = projectContext()
    const projectId = selectedProject.value?.id
    const areaId = selectedArea.value?.id
    const riskId = selectedRisk.value?.id
    const scenarioId = selectedScenario.value?.id
    if (!projectId || !areaId || !riskId || !scenarioId)
      return
    const { data } = await request(posting, () => v1ProjectsControllerAddImageToScenario({
      body: { image: withUploadSafeName(file) },
      headers: adminHeaders(),
      path: { areaId, id: projectId, riskId, scenarioId },
    }))
    const image = data
    if (image) {
      const storedFile = new File([file], `${image.id}.jpg`, { type: file.type })
      const images = scenarioImages.value.get(scenarioId) ?? []
      scenarioImages.value.set(scenarioId, [...images, storedFile])
    }
    return image
  }

  async function loadScenarioImages(
    fetching: Ref<boolean>,
    options: LoadScenarioImageOptions = {},
  ) {
    const { selectedProject } = projectContext()
    const context = options.context
    const projectId = context?.projectId ?? selectedProject.value?.id
    const areaId = context?.areaId ?? selectedArea.value?.id
    const riskId = context?.riskId ?? selectedRisk.value?.id
    const scenarioId = context?.scenarioId ?? selectedScenario.value?.id
    const images = context?.images ?? selectedScenario.value?.images
    if (!projectId || !areaId || !riskId || !scenarioId || !images?.length)
      return
    if (scenarioImages.value.get(scenarioId)?.length === images.length)
      return
    scenarioImages.value.set(scenarioId, [])
    await Promise.all(images.map(async (image: ScenarioImage) => {
      const publicUrl = options.publicAssetUrl?.(`scenario-images/${image.id}`)
      let blob: Blob | undefined
      if (publicUrl) {
        blob = await $fetch<Blob>(publicUrl, { responseType: 'blob' })
      }
      else {
        const result = await request(fetching, () =>
          v1ProjectsControllerGetScenarioImage({
            ...(options.accessToken ? { auth: options.accessToken } : {}),
            parseAs: 'blob',
            path: {
              areaId,
              id: projectId,
              riskId,
              scenarioId,
              scenarioImageId: image.id,
            },
          }) as Promise<ApiResult<Blob>>)
        blob = result.data
      }
      if (blob)
        scenarioImages.value.get(scenarioId)?.push(new File([blob], `${image.id}.jpg`, { type: blob.type }))
    }))
  }

  async function removeScenarioImage(deleting: Ref<boolean>, imageId?: string) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id || !selectedScenario.value?.id || !imageId)
      return
    await request(deleting, () => v1ProjectsControllerDeleteScenarioImage({
      headers: adminHeaders(),
      path: {
        areaId: selectedArea.value!.id!,
        id: selectedProject.value!.id,
        imageId,
        riskId: selectedRisk.value!.id!,
        scenarioId: selectedScenario.value!.id!,
      },
    }))
  }

  async function fetchSuggestedScenarios(fetching: Ref<boolean>) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !selectedRisk.value?.id)
      return []
    const { data } = await request(fetching, () =>
      v1ProjectsControllerGetSuggestedScenariosForRisk({
        path: {
          areaId: selectedArea.value!.id!,
          id: selectedProject.value!.id,
          riskId: selectedRisk.value!.id!,
        },
      }))
    const scenarios = data?.scenarioTypes ?? []
    selectedRisk.value.suggestedScenarios = scenarios
    return scenarios
  }

  async function synchronizeScenarios(
    saving: Ref<boolean>,
    selectedTypes: ScenarioType[],
  ) {
    const { selectedProject, showNotification } = projectContext()
    const projectId = selectedProject.value?.id
    const areaId = selectedArea.value?.id
    const riskId = selectedRisk.value?.id
    if (!projectId || !areaId || !riskId || !selectedRisk.value)
      return
    const oldScenarios = selectedRisk.value.scenarios ?? []
    const added = selectedTypes.filter(type =>
      !oldScenarios.map(scenario => scenario.scenarioType).includes(type))
    const { data, success } = await request<unknown>(saving, async () => {
      await Promise.all(oldScenarios
        .filter(scenario => scenario.id && !selectedTypes.includes(scenario.scenarioType))
        .map(scenario => v1ProjectsControllerDeleteScenario({
          headers: adminHeaders(),
          path: { areaId, id: projectId, riskId, scenarioId: scenario.id! },
          throwOnError: true,
        })))
      await Promise.all(added
        .filter((type): type is ScenarioType & { id: string } => Boolean(type.id))
        .map(type => v1ProjectsControllerCreateScenario({
          body: { scenarioTypeId: type.id },
          headers: adminHeaders(),
          path: { areaId, id: projectId, riskId },
          throwOnError: true,
        })))
      await Promise.all(added.filter(type => !type.id).map(type =>
        v1ProjectsControllerCreateCustomScenario({
          body: { name: type.name },
          headers: adminHeaders(),
          path: { areaId, id: projectId, riskId },
          throwOnError: true,
        })))
      return v1ProjectsControllerGetScenarios({
        path: { areaId, id: projectId, riskId },
        throwOnError: true,
      }) as Promise<ApiResult<unknown>>
    })
    if (success) {
      selectedRisk.value.scenarios = ((data as { scenarios?: Scenario[] } | undefined)?.scenarios ?? [])
      showNotification.value = true
      return selectedRisk.value.scenarios
    }
  }

  async function renameArea(updating: Ref<boolean>, name: string) {
    const { selectedProject, showNotification } = projectContext()
    if (!selectedProject.value?.id || !selectedArea.value?.id || !name)
      return false
    const { success } = await request(updating, () => v1ProjectsControllerUpdateAreaName({
      body: { name },
      path: { areaId: selectedArea.value!.id!, id: selectedProject.value!.id },
    }))
    if (success) {
      // The backend renames the AreaType of new-shape custom areas and the
      // area itself for old-shape ones; mirror whichever it wrote.
      if (selectedArea.value.areaType?.isCustom)
        selectedArea.value.areaType.name = name
      else
        selectedArea.value.name = name
      showNotification.value = true
      AnalyticsLogger.send('area_renamed', {
        item_name: name,
        risk_value_chain_name: selectedProject.value.riskValueChain.name,
        project_name: selectedProject.value.name,
        area_name: name,
        status: 'Renamed',
      })
    }
    return success
  }

  async function synchronizeAreaRisks(
    loading: Ref<boolean>,
    addedRiskTypeIds: string[],
    removedRiskTypeIds: string[],
  ) {
    const { selectedProject, showNotification } = projectContext()
    const projectId = selectedProject.value?.id
    const areaId = selectedArea.value?.id
    if (!projectId || !areaId || !selectedArea.value)
      return
    const { data, success } = await request<unknown>(loading, async () => {
      for (const riskTypeId of removedRiskTypeIds) {
        const risk = selectedArea.value?.risks?.find(item => item.riskType.id === riskTypeId)
        if (risk?.id) {
          await v1ProjectsControllerDeleteRisk({
            headers: adminHeaders(),
            path: { areaId, id: projectId, riskId: risk.id },
            throwOnError: true,
          })
        }
      }
      if (!addedRiskTypeIds.length)
        return { data: selectedArea.value }
      return v1ProjectsControllerAddRisks({
        body: { riskTypeIds: addedRiskTypeIds },
        headers: adminHeaders(),
        path: { areaId, id: projectId },
        throwOnError: true,
      }) as Promise<ApiResult<unknown>>
    })
    if (success) {
      if (removedRiskTypeIds.length) {
        selectedArea.value.risks = selectedArea.value.risks
          ?.filter(risk => !removedRiskTypeIds.includes(risk.riskType.id))
      }
      if (addedRiskTypeIds.length && data)
        selectedArea.value = data as Area
      const projectArea = selectedProject.value?.areas?.find(area => area.id === areaId)
      if (projectArea)
        projectArea.risks = selectedArea.value.risks
      showNotification.value = true
    }
  }

  async function applyAreaRiskChanges(
    loading: Ref<boolean>,
    areas: Area[],
    removedAreas: Area[],
    removedRisks: RemovedRisk[],
  ) {
    const { selectedProject } = projectContext()
    if (!selectedProject.value?.id)
      return
    const projectId = selectedProject.value.id
    const { success } = await request(loading, async () => {
      await Promise.all(removedRisks
        .filter((risk): risk is RemovedRisk & { areaId: string, riskId: string } => Boolean(risk.areaId && risk.riskId))
        .map(risk => v1ProjectsControllerDeleteRisk({
          headers: adminHeaders(),
          path: { areaId: risk.areaId, id: projectId, riskId: risk.riskId },
          throwOnError: true,
        })))
      await Promise.all(removedAreas
        .filter((area): area is Area & { id: string } => Boolean(area.id))
        .map(area => v1ProjectsControllerDeleteArea({
          headers: adminHeaders(),
          path: { areaId: area.id, id: projectId },
          throwOnError: true,
        })))
      return { data: undefined }
    })
    if (success) {
      const removedAreaIds = new Set(removedAreas.map(area => area.id))
      const removedRiskIds = new Set(removedRisks.map(risk => risk.riskId))
      selectedProject.value.areas = selectedProject.value.areas
        ?.filter(area => !area.id || !removedAreaIds.has(area.id))
        .map(area => ({
          ...area,
          risks: area.risks?.filter(risk => !risk.id || !removedRiskIds.has(risk.id)),
        }))
      await createAreasAndRisks(loading, areas, projectId)
    }
  }

  return {
    selectedArea,
    selectedRisk,
    selectedScenario,
    suggestedAreas,
    suggestedRisks,
    allRiskTypes,
    suggestedSolutionTypes,
    scenarioImages,
    resetAssessment,
    clearAssessmentCache,
    fetchSuggestedAreas,
    fetchSuggestedRiskTypes,
    fetchAllRiskTypes,
    fetchSuggestedSolutionTypes,
    createAreasAndRisks,
    patchRisk,
    deleteRisk,
    deleteArea,
    patchScenario,
    renameScenario,
    deleteScenario,
    deleteSolution,
    createBulkSolution,
    createCustomScenarios,
    createCustomSolution,
    uploadScenarioImages,
    loadScenarioImages,
    removeScenarioImage,
    fetchSuggestedScenarios,
    synchronizeScenarios,
    renameArea,
    synchronizeAreaRisks,
    applyAreaRiskChanges,
  }
})
